import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppApis } from '@app/core/APIs/app-apis';
import type {
  OutboxDeliveryDetail,
  OutboxMonitorPage,
  OutboxMonitorStatus,
  OutboxReplayRequest,
  OutboxSummary,
  OutboxWorkerState,
  OutboxWorkerUpdate,
  OutboxWorkerAudit,
} from './platform-operations.models';
const options = {
  withCredentials: true,
  headers: new HttpHeaders({
    'skip-error-interceptor': 'yes',
    'skip-loading-interceptor': 'yes',
  }),
};
const writeOptions = (eTag: string, key: string) => ({
  ...options,
  headers: options.headers.set('If-Match', eTag).set('Idempotency-Key', key),
});
@Injectable({ providedIn: 'root' })
export class PlatformOperationsService {
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  getSummary() {
    return this.http.get<OutboxSummary>(
      this.apis.core.manual.getPlatformOutboxSummary(),
      options,
    );
  }
  getMonitor(status: OutboxMonitorStatus, cursor?: string) {
    return this.http.get<OutboxMonitorPage>(
      this.apis.core.manual.getPlatformOutboxMonitor({
        status,
        take: 50,
        cursor,
      }),
      options,
    );
  }
  getDelivery(eventId: string, consumerId: string) {
    return this.http.get<OutboxDeliveryDetail>(
      this.apis.core.manual.getPlatformOutboxDelivery(eventId, consumerId),
      options,
    );
  }
  getWorker() {
    return this.http.get<OutboxWorkerState>(
      this.apis.core.manual.getPlatformOutboxWorker(),
      options,
    );
  }
  getWorkerHistory() {
    return this.http.get<OutboxWorkerAudit[]>(
      `${this.apis.core.manual.getPlatformOutboxWorker()}/history`,
      options,
    );
  }
  retryInvocation(
    eventId: string,
    consumerId: string,
    eTag: string,
    body: OutboxReplayRequest,
    key: string,
  ) {
    return this.http.post<void>(
      this.apis.core.manual.postPlatformOutboxReplay(eventId, consumerId),
      body,
      writeOptions(eTag, key),
    );
  }
  updateWorker(eTag: string, body: OutboxWorkerUpdate, key: string) {
    return this.http.put<OutboxWorkerState>(
      this.apis.core.manual.getPlatformOutboxWorker(),
      body,
      writeOptions(eTag, key),
    );
  }
}
